import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ZipDetail from './ZipDetail.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useUnits } from '../lib/preferences.js';

// Below the mobile breakpoint (see useIsMobile), the detail panel is
// portaled straight to document.body instead of rendered inline in the
// table (see below) — a position:fixed overlay nested inside .table-wrap's
// horizontal scroll container proved unreliable on real mobile Safari, even
// though it tested clean in desktop-Chromium's device emulation. Portaling
// it out of the table's DOM subtree entirely sidesteps that class of bug
// rather than fighting it with more CSS.

const COLUMNS = [
  { key: 'zip', label: 'ZIP / Area', caLabel: 'Municipality', align: 'left', sortable: true },
  { key: 'wetBulbF', kind: 'temp', label: 'Wet bulb', digits: 1, emphasis: true },
  { key: 'tempF', kind: 'temp', label: 'Air temp', digits: 0 },
  { key: 'humidity', label: 'RH', unit: '%', digits: 0 },
  { key: 'wbgtSunF', kind: 'temp', label: 'WBGT est.', digits: 1 },
  { key: 'heatIndexF', kind: 'temp', label: 'Heat idx', digits: 0 },
  { key: 'todayHighF', kind: 'temp', label: 'High', digits: 0 },
  { key: 'todayLowF', kind: 'temp', label: 'Low', digits: 0 },
  { key: 'aqiPm25', label: 'AQI PM2.5', digits: 0 },
  { key: 'gustMph', kind: 'speed', label: 'Gusts', digits: 0 },
  { key: 'precip24In', kind: 'precip', label: 'Rain next 24h', digits: 2 },
  { key: 'uvIndex', label: 'UV', digits: 0 },
  { key: 'flags', label: 'Triggers', sortable: false },
];

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'official', label: 'Official alerts', title: 'Places with an active official alert (NWS / ECCC)' },
  { key: 'heat', label: 'Heat' },
  { key: 'air', label: 'Air' },
  { key: 'wind', label: 'Wind' },
  { key: 'stormwater', label: 'Stormwater permit', title: 'Stormwater permit / inspection planning' },
  { key: 'lightning', label: 'Lightning' },
  { key: 'cold', label: 'Cold' },
];

// Stormwater (a permit inspection-planning signal) and lightning (a worker
// safety stand-down) are separate categories and never share a chip.
const CATEGORY_ABBR = {
  heat: 'HEAT',
  air: 'AIR',
  wind: 'WIND',
  stormwater: 'STORMWATER',
  lightning: 'LIGHTNING',
  uv: 'UV',
  cold: 'COLD',
};
const CATEGORY_TITLE = { stormwater: 'Stormwater permit / inspection planning', lightning: 'Lightning safety' };

function num(v, digits) {
  return v == null ? <span className="muted">—</span> : v.toFixed(digits);
}

export default function ZipTable({ zips, selected, onSelect }) {
  const [sort, setSort] = useState({ key: 'wetBulbF', dir: 'desc' });
  const [filter, setFilter] = useState('all');
  const isMobile = useIsMobile();
  const u = useUnits();
  const isCa = zips[0]?.country === 'CA';
  // Header unit per column kind; values are converted for display only
  // (sorting still uses the raw °F / mph / in values, same order).
  const unitOf = (c) => (c.kind === 'temp' ? u.tempLabel : c.kind === 'speed' ? u.speedLabel : c.kind === 'precip' ? u.precipLabel : c.unit);
  const open = selected;
  const setOpen = onSelect;
  const rowRefs = useRef({});

  // A selection made elsewhere (e.g. a ?zip= deep link) needs the matching row to
  // scroll into view, since the table can be long and the map sits above
  // it — a table-originated click is already in view, so this is a no-op
  // smooth-scroll in that case rather than something to special-case.
  useEffect(() => {
    if (selected) rowRefs.current[selected]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selected]);

  const rows = useMemo(() => {
    let list = zips;

    if (filter === 'attention') {
      list = list.filter((z) => z.severity >= 2);
    } else if (filter === 'official') {
      list = list.filter((z) => z.officialAlerts?.length > 0);
    } else if (filter !== 'all') {
      list = list.filter((z) => z.rules.some((r) => r.category === filter && r.severity >= 1));
    }

    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (key === 'zip') return mul * a.zip.localeCompare(b.zip);
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return mul * (av - bv);
    });
  }, [zips, sort, filter]);

  const toggleSort = (key) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' },
    );

  return (
    <>
      <div className="controls">
        <div className="seg">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={filter === f.key ? 'on' : ''}
              title={f.title}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>
          {rows.length} of {zips.length} ZIP codes
        </span>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={c.align === 'left' ? 'left' : ''}
                  onClick={() => c.sortable !== false && toggleSort(c.key)}
                  style={c.sortable === false ? { cursor: 'default' } : undefined}
                  title={c.sortable === false ? undefined : `Sort by ${c.label}`}
                >
                  {isCa && c.caLabel ? c.caLabel : c.label}
                  {unitOf(c) ? ` ${unitOf(c)}` : ''}
                  {sort.key === c.key && (
                    <span className="arrow">{sort.dir === 'desc' ? '▾' : '▴'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((z) => {
              const isOpen = open === z.zip;
              const cats = [...new Set(z.rules.filter((r) => r.severity >= 1).map((r) => r.category))];
              return (
                <Fragment key={z.zip}>
                  <tr
                    ref={(el) => {
                      rowRefs.current[z.zip] = el;
                    }}
                    className={isOpen ? 'open' : ''}
                    onClick={() => setOpen(isOpen ? null : z.zip)}
                  >
                    <td className="left">
                      <span className={`dot sev-${z.severity}`} />
                      {z.kind === 'municipality' ? (
                        <span className="zip-code">{z.place}</span>
                      ) : (
                        <>
                          <span className="zip-code">{z.zip}</span>
                          <span className="place">{z.place}</span>
                        </>
                      )}
                    </td>
                    <td className={`val-strong wb-${z.wetBulbBand?.key ?? 'normal'}`}>
                      {num(u.temp(z.wetBulbF), 1)}
                    </td>
                    <td>{num(u.temp(z.tempF), 0)}</td>
                    <td>{num(z.humidity, 0)}</td>
                    <td>{num(u.temp(z.wbgtSunF), 1)}</td>
                    <td>{num(u.temp(z.heatIndexF), 0)}</td>
                    <td>{num(u.temp(z.todayHighF), 0)}</td>
                    <td>{num(u.temp(z.todayLowF), 0)}</td>
                    <td>{num(z.aqiPm25, 0)}</td>
                    <td>{num(u.speed(z.gustMph), 0)}</td>
                    <td>{num(u.precip(z.precip24In), u.precipDigits(2))}</td>
                    <td>{num(z.uvIndex, 0)}</td>
                    <td>
                      <div className="chips">
                        {z.officialAlerts?.length > 0 && (
                          <span
                            className="chip chip-official"
                            title={`Official ${z.officialAlerts[0].provider ?? 'NWS'}: ${z.officialAlerts.map((a) => a.event).join(', ')}`}
                          >
                            {z.officialAlerts[0].provider ?? 'NWS'} {z.officialAlerts.length > 1 ? `×${z.officialAlerts.length}` : z.officialAlerts[0].event.toUpperCase()}
                          </span>
                        )}
                        {z.officialAlerts === null && (
                          <span className="chip chip-unavailable" title="Official alert feed unavailable — not an all-clear">
                            NWS ?
                          </span>
                        )}
                        {cats.length === 0 && !z.officialAlerts?.length && z.officialAlerts !== null && (
                          <span className="muted">—</span>
                        )}
                        {cats.map((c) => {
                          const worst = Math.max(
                            ...z.rules.filter((r) => r.category === c).map((r) => r.severity),
                          );
                          return (
                            <span key={c} className={`chip sev-${worst}`} title={CATEGORY_TITLE[c]}>
                              {CATEGORY_ABBR[c] ?? c}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                  {isOpen &&
                    (isMobile
                      ? createPortal(
                          <ZipDetail zip={z} onClose={() => setOpen(null)} />,
                          document.body,
                        )
                      : (
                        <tr className="detail">
                          <td colSpan={COLUMNS.length}>
                            <ZipDetail zip={z} onClose={() => setOpen(null)} />
                          </td>
                        </tr>
                        ))}
                </Fragment>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="left muted" style={{ padding: 28 }}>
                  No ZIP codes match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
